import React, { useEffect, useState } from 'react';
import { Link, useHistory } from 'react-router-dom';
import Modal from 'react-bootstrap/Modal';
import DrLicitaConfirm from 'components/molecules/dr_licita_confirm';
import Loader from 'components/atoms/loader';
import moment from 'moment';

import {
  DrLicitaChatSummary,
  fetchDrLicitaChats,
  tituloConversa,
} from 'services/actions/dr_licita_chat_actions';
import {
  createDrLicitaProjeto,
  deleteDrLicitaProjeto,
  DrLicitaProjeto,
  fetchDrLicitaProjetos,
  renameDrLicitaProjeto,
  setChatProjeto,
} from 'services/actions/dr_licita_projetos_actions';

import * as Shell from '../DrLicitaChat/styles';
import * as S from '../DrLicitaPeticoes/styles';

// Página "Projetos" da aplicação Dr. Licita 3.0 (Figma 1768:4610/4621):
// projetos segmentam as conversas do chat; dentro do projeto ficam as
// conversas movidas para ele (a exclusão devolve tudo ao histórico).

const MODES = [
  { mode: 'peticao', title: 'Construir Petição', icon: 'edit_note', disabled: false },
  { mode: 'duvidas', title: 'Dúvidas Jurídicas', icon: 'help_outline', disabled: false },
  { mode: 'jurisprudencia', title: 'Jurisprudência Fácil', icon: 'gavel', disabled: false },
];

const rotaDoModo = (mode: string) =>
  mode === 'jurisprudencia' ? '/dr_licita/jurisprudencia' : `/dr_licita/chat?mode=${mode}`;

type ModalState = { kind: 'novo' } | { kind: 'renomear'; projeto: DrLicitaProjeto } | null;

const DrLicitaProjetos: React.FC = () => {
  const history = useHistory();

  const [projetos, setProjetos] = useState<DrLicitaProjeto[]>([]);
  const [chats, setChats] = useState<DrLicitaChatSummary[]>([]);
  const [carregando, setCarregando] = useState(true);
  const [aberto, setAberto] = useState<DrLicitaProjeto | null>(null);
  const [modal, setModal] = useState<ModalState>(null);
  const [modalValue, setModalValue] = useState('');
  const [sending, setSending] = useState(false);
  const [aviso, setAviso] = useState<string | null>(null);
  const [confirmacao, setConfirmacao] = useState<{
    titulo: string;
    mensagem: string;
    acao: () => void;
  } | null>(null);

  const mostrarAviso = (texto: string) => {
    setAviso(texto);
    window.setTimeout(() => setAviso(null), 3500);
  };

  const recarregar = async () => {
    setCarregando(true);
    await Promise.all([
      fetchDrLicitaProjetos().then(setProjetos),
      fetchDrLicitaChats().then(setChats),
    ]);
    setCarregando(false);
  };

  useEffect(() => {
    document.title = 'Dr Licita - Projetos';
    recarregar();
  }, []);

  const confirmarModal = async () => {
    if (!modal) return;
    const valor = modalValue.trim();
    if (!valor) return;
    setSending(true);
    if (modal.kind === 'novo') {
      await createDrLicitaProjeto(valor);
    } else {
      await renameDrLicitaProjeto(modal.projeto.id, valor);
      if (aberto?.id === modal.projeto.id) setAberto({ ...aberto, name: valor });
    }
    setSending(false);
    setModal(null);
    setModalValue('');
    recarregar();
  };

  const excluirProjeto = (projeto: DrLicitaProjeto) => {
    setConfirmacao({
      titulo: 'Excluir projeto',
      mensagem: `Deseja excluir o projeto "${projeto.name}"? As conversas voltam para o histórico.`,
      acao: async () => {
        setConfirmacao(null);
        await deleteDrLicitaProjeto(projeto.id);
        if (aberto?.id === projeto.id) setAberto(null);
        recarregar();
      },
    });
  };

  const tirarDoProjeto = async (chatId: number) => {
    const ok = await setChatProjeto(chatId, null);
    mostrarAviso(
      ok ? 'Conversa devolvida ao histórico geral.' : 'Não foi possível mover. Tente novamente.',
    );
    recarregar();
  };

  const conversasDoProjeto = aberto ? chats.filter((chat) => chat.project_id === aberto.id) : [];

  const renderModal = () => {
    if (!modal) return null;
    return (
      <Modal show centered onHide={() => setModal(null)}>
        <S.ModalContainer>
          <div className="modal-header">
            <h5 className="modal-title text-nowrap">
              <strong>{modal.kind === 'novo' ? 'Novo Projeto' : 'Renomear Projeto'}</strong>
            </h5>
            <button
              aria-label="Close"
              className="close"
              data-dismiss="modal"
              type="button"
              onClick={() => setModal(null)}
            >
              <span aria-hidden="true">×</span>
            </button>
          </div>
          <S.ModalBody>
            <label htmlFor="projetos-modal-input">
              Nome
              <input
                id="projetos-modal-input"
                type="text"
                value={modalValue}
                ref={(element) => element?.focus()}
                onChange={(event) => setModalValue(event.target.value)}
                onKeyDown={(event) => {
                  if (event.key === 'Enter') confirmarModal();
                }}
              />
            </label>
          </S.ModalBody>
          <div className="modal-footer justify-content-center">
            <button
              type="button"
              className="btn btn-sm btn-primary"
              disabled={sending}
              onClick={confirmarModal}
            >
              {modal.kind === 'novo' ? 'Criar Projeto' : 'Salvar'}
            </button>
          </div>
        </S.ModalContainer>
      </Modal>
    );
  };

  return (
    <Shell.Page>
      <Shell.Sidebar>
        <h1>Dr.Licita</h1>
        <Shell.SidebarScroll>
          <Shell.SidebarButton as={Link} to="/dr_licita/chat">
            <i className="material-icons">add</i>
            Nova conversa
          </Shell.SidebarButton>
          <Shell.SidebarButton as={Link} to="/dr_licita/peticoes">
            <i className="material-icons">description</i>
            Petições
          </Shell.SidebarButton>
          <Shell.SidebarButton active>
            <i className="material-icons">folder_open</i>
            Projetos
          </Shell.SidebarButton>
          <Shell.SidebarButton as={Link} to="/dr_licita/biblioteca">
            <i className="material-icons">menu_book</i>
            Biblioteca de Prompts
          </Shell.SidebarButton>

          <h2>Acesso rápido</h2>
          {MODES.map((item) =>
            item.disabled ? (
              <Shell.SidebarButton key={item.mode} disabled>
                <i className="material-icons">{item.icon}</i>
                {item.title}
                <small>em breve</small>
              </Shell.SidebarButton>
            ) : (
              <Shell.SidebarButton key={item.mode} as={Link} to={rotaDoModo(item.mode)}>
                <i className="material-icons">{item.icon}</i>
                {item.title}
              </Shell.SidebarButton>
            ),
          )}
        </Shell.SidebarScroll>

        <Shell.SidebarFooter>
          <button type="button" title="Em breve">
            <i className="material-icons">play_circle_outline</i>
            Veja como funciona
          </button>
        </Shell.SidebarFooter>
      </Shell.Sidebar>

      <Shell.Main>
        <S.Content>
          {aberto ? (
            <S.Breadcrumb>
              <button type="button" title="Voltar para Projetos" onClick={() => setAberto(null)}>
                <i className="material-icons">arrow_back</i>
                Projetos
              </button>
              <span>&gt; {aberto.name}</span>
            </S.Breadcrumb>
          ) : (
            <h2>Projetos</h2>
          )}

          <S.Toolbar>
            <span />
            {!aberto && (
              <S.ToolbarButton
                type="button"
                onClick={() => {
                  setModalValue('');
                  setModal({ kind: 'novo' });
                }}
              >
                <i className="material-icons">create_new_folder</i>
                Novo Projeto
              </S.ToolbarButton>
            )}
          </S.Toolbar>

          <S.Table>
            <S.TableHead>
              <span>Nome</span>
              <span>{aberto ? 'Petição' : 'Conversas'}</span>
              <span>Atualização</span>
              <span />
            </S.TableHead>

            {!carregando &&
              !aberto &&
              projetos.map((projeto) => (
                <S.Row key={projeto.id}>
                  <S.NameCell type="button" onClick={() => setAberto(projeto)}>
                    <i className="material-icons">folder</i>
                    {projeto.name}
                  </S.NameCell>
                  <S.Processo>{projeto.conversas}</S.Processo>
                  <S.Data>
                    {moment(projeto.last_activity || projeto.created_at)
                      .subtract(3, 'hours')
                      .format('DD/MM/YYYY')}
                  </S.Data>
                  <S.Actions>
                    <button type="button" title="Abrir projeto" onClick={() => setAberto(projeto)}>
                      <i className="material-icons">folder_open</i>
                    </button>
                    <button
                      type="button"
                      title="Renomear"
                      onClick={() => {
                        setModalValue(projeto.name);
                        setModal({ kind: 'renomear', projeto });
                      }}
                    >
                      <i className="material-icons">edit</i>
                    </button>
                    <button type="button" title="Excluir" onClick={() => excluirProjeto(projeto)}>
                      <i className="material-icons">delete_outline</i>
                    </button>
                  </S.Actions>
                </S.Row>
              ))}

            {carregando && (
              <S.Empty>
                <Loader />
              </S.Empty>
            )}

            {!carregando && !aberto && projetos.length === 0 && (
              <S.Empty>
                Você ainda não possui projetos. Crie um para organizar suas conversas.
              </S.Empty>
            )}

            {!carregando &&
              aberto &&
              conversasDoProjeto.map((chat) => (
                <S.Row key={chat.id}>
                  <S.NameCell
                    type="button"
                    onClick={() => history.push(`/dr_licita/chat?chat=${chat.id}`)}
                  >
                    <i className="material-icons">chat_bubble_outline</i>
                    {tituloConversa(chat)}
                  </S.NameCell>
                  <S.Processo>
                    {chat.document_id ? (
                      <Link to={`/dr_licita/peticoes?document=${chat.document_id}`}>Petição</Link>
                    ) : (
                      '—'
                    )}
                  </S.Processo>
                  <S.Data>
                    {chat.last_message_at
                      ? moment(chat.last_message_at).subtract(3, 'hours').format('DD/MM/YYYY')
                      : '—'}
                  </S.Data>
                  <S.Actions>
                    <button
                      type="button"
                      title="Abrir conversa"
                      onClick={() => history.push(`/dr_licita/chat?chat=${chat.id}`)}
                    >
                      <i className="material-icons">visibility</i>
                    </button>
                    {chat.document_id && (
                      <button
                        type="button"
                        title="Abrir a petição vinculada"
                        onClick={() =>
                          history.push(`/dr_licita/peticoes?document=${chat.document_id}`)
                        }
                      >
                        <i className="material-icons">description</i>
                      </button>
                    )}
                    <button
                      type="button"
                      title="Tirar do projeto (volta ao histórico geral)"
                      onClick={() => tirarDoProjeto(chat.id)}
                    >
                      <i className="material-icons">drive_file_move_rtl</i>
                    </button>
                  </S.Actions>
                </S.Row>
              ))}

            {!carregando && aberto && conversasDoProjeto.length === 0 && (
              <S.Empty>
                Projeto vazio — mova conversas para cá pelo histórico do chat (ícone de pasta).
              </S.Empty>
            )}
          </S.Table>
        </S.Content>
      </Shell.Main>

      {renderModal()}
      {confirmacao && (
        <DrLicitaConfirm
          titulo={confirmacao.titulo}
          mensagem={confirmacao.mensagem}
          onCancelar={() => setConfirmacao(null)}
          onConfirmar={confirmacao.acao}
        />
      )}
      {aviso && <Shell.Toast>{aviso}</Shell.Toast>}
    </Shell.Page>
  );
};

export default DrLicitaProjetos;
